import React from 'react';
import { Box, Typography } from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { styled } from '@mui/material/styles';
import { motion } from 'framer-motion';
import ExecutiveCard from '../../../components/ExecutiveCard/index.tsx';
import { Executive } from '../../../types/executive.ts';

interface ExecutiveBoardProps {
  section?: string;
  executives: Executive[];
  title?: string;
  onSelectExecutive?: (executive: Executive) => void;
}

const BoardContainer = styled(Box)(({ theme }) => ({
  width: '100%',
  position: 'relative',
  padding: theme.spacing(3),
  background: 'rgba(26, 26, 46, 0.6)',
  backdropFilter: 'blur(10px)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(138, 43, 226, 0.2)',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: '-50%',
    left: '-50%',
    width: '200%',
    height: '200%',
    background: 'linear-gradient(0deg, transparent, transparent 30%, rgba(0, 255, 255, 0.3))',
    opacity: 0,
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, #003366, #00CC99, #8A2BE2, #FF6F00)',
    backgroundSize: '300% 100%',
    animation: 'gradient 6s linear infinite',
  },
  '&:hover': {
    boxShadow: '0 0 20px rgba(138, 43, 226, 0.3)',
    '&::before': {
      opacity: 1,
      transform: 'rotate(-45deg) translateY(100%)',
    },
  },
  '@keyframes gradient': {
    '0%': {
      backgroundPosition: '0% 50%'
    },
    '50%': {
      backgroundPosition: '100% 50%'
    },
    '100%': {
      backgroundPosition: '0% 50%'
    }
  }
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#fff',
  marginBottom: theme.spacing(2),
  fontWeight: 600,
  position: 'relative',
  display: 'inline-block',
  '&::after': {
    content: '""',
    position: 'absolute',
    bottom: -5,
    left: 0,
    width: '100%',
    height: '2px',
    background: 'linear-gradient(90deg, #00CC99, #8A2BE2)',
  }
}));

const ExecutiveBoard: React.FC<ExecutiveBoardProps> = ({ section = '', executives, title, onSelectExecutive }) => {
  // Filter executives by section if needed
  const filteredExecutives = executives.filter(exec => exec.section === section);
  
  // Create a formatted section title
  const sectionTitle = title || (section ? `${section.charAt(0).toUpperCase() + section.slice(1)} Executives` : 'Executives');
  
  return (
    <BoardContainer>
      <SectionTitle variant="h6">
        {sectionTitle}
      </SectionTitle>
      <Grid container spacing={2} alignItems="flex-start">
        {filteredExecutives.map((executive, index) => (
          <Grid xs={12} key={executive.id}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3, delay: index * 0.1 }}
            >
              <ExecutiveCard 
                executive={executive} 
                onClick={() => onSelectExecutive && onSelectExecutive(executive)} 
              />
            </motion.div>
          </Grid>
        ))}
      </Grid>
    </BoardContainer>
  );
};

export default ExecutiveBoard; 